import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { calculateDeadline, calculateCountdown } from './complianceMath'; 

// --- DATE MATH HELPERS ---
const getMeetingScheduleDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  if (!year || !month || !day) return '';

  let d = new Date(year, month - 1, day);
  d.setDate(d.getDate() - 28); 
  return d.toISOString().split('T')[0];
};

const formatDateString = (isoString) => {
  if (!isoString) return 'Awaiting Data';
  const [year, month, day] = isoString.split('-');
  return `${month}/${day}/${year}`;
};

const StudentCard = ({ student, tenantId, caseManagerName, eceRecorderName, currentUserRole, onDelete, calendar, schools = [], staffList = [], eceRecordersList = [], onTransfer }) => {
  
  // --- Local Memory for the UI ---
  const [savedData, setSavedData] = useState({
    annualDate: student.annualIepDate || '',
    reevalDate: student.reevalDate || '',
    consentDate: student.consentDate || '',
    consentObtainedDate: student.consentObtainedDate || '', 
    initialDueDate: student.initialDueDate || '',
    initialTracking: student.initialTracking || false
  });

  // --- Working Drafts (Used ONLY in Edit Mode) ---
  const [annualDate, setAnnualDate] = useState(savedData.annualDate);
  const [reevalDate, setReevalDate] = useState(savedData.reevalDate);
  const [consentDate, setConsentDate] = useState(savedData.consentDate); 
  const [consentObtainedDate, setConsentObtainedDate] = useState(savedData.consentObtainedDate);
  const [initialTracking, setInitialTracking] = useState(savedData.initialTracking);
  const [calculatedInitialDue, setCalculatedInitialDue] = useState(savedData.initialDueDate);
  
  // UI toggles
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  
  // NEW: Quick action UI toggles
  const [showConsentPicker, setShowConsentPicker] = useState(false); 
  const [tempConsentDate, setTempConsentDate] = useState('');

  // Transfer State
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferSchoolId, setTransferSchoolId] = useState(student.schoolId || '');
  const [transferManagerId, setTransferManagerId] = useState(student.assignedCaseManager || '');
  const [transferEceRecorderId, setTransferEceRecorderId] = useState(student.assignedEceRecorder || ''); 

  // Calculate Initial Deadline dynamically while typing in edit mode
  useEffect(() => {
    if (initialTracking && consentDate && calendar && calendar.holidaysAndBreaks) {
      const deadline = calculateDeadline(consentDate, 60, calendar.holidaysAndBreaks, calendar.yearBoundaries);
      setCalculatedInitialDue(deadline); 
    } else if (!consentDate) {
      setCalculatedInitialDue('');
    }
  }, [consentDate, calendar, initialTracking]);

  // --- Re-Evaluation Continuous Countdown ---
  const computedConsentDue = savedData.reevalDate && calendar 
    ? calculateCountdown(savedData.reevalDate, 80, calendar.holidaysAndBreaks || [], calendar.yearBoundaries || {})
    : '';

  const computedMeetingDate = getMeetingScheduleDate(savedData.reevalDate);

  const getDaysDifference = (dateString) => {
    if (!dateString) return null;
    const target = new Date(dateString);
    target.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
  };

  const consentDiffDays = getDaysDifference(computedConsentDue);
  
  // Warnings vanish if consent has been obtained
  const isConsentWarning = !savedData.consentObtainedDate && consentDiffDays !== null && consentDiffDays <= 14 && consentDiffDays >= 0;
  const isConsentOverdue = !savedData.consentObtainedDate && consentDiffDays !== null && consentDiffDays < 0;

  // Status Checkers 
  const getAnnualStatus = () => {
    const diffDays = getDaysDifference(savedData.annualDate);
    if (diffDays === null) return { color: '#ccc', text: 'No Date Set', weight: 0 };
    if (diffDays < 0) return { color: '#8b0000', text: `Past Due (${Math.abs(diffDays)} days)`, weight: 4 };
    if (diffDays <= 14) return { color: '#ba0c2f', text: `Critical (${diffDays} days)`, weight: 3 };
    if (diffDays <= 30) return { color: '#ff9900', text: `Warning (${diffDays} days)`, weight: 2 };
    return { color: '#008000', text: 'Compliant', weight: 1 };
  };

  const getReevalStatus = () => {
    const diffDays = getDaysDifference(savedData.reevalDate);
    if (diffDays === null) return { color: '#ccc', text: 'No Date Set', weight: 0 };
    if (diffDays < 0) return { color: '#8b0000', text: `Past Due (${Math.abs(diffDays)} days)`, weight: 4 };
    if (diffDays <= 30) return { color: '#ba0c2f', text: `Critical (${diffDays} days)`, weight: 3 };
    if (diffDays <= 60) return { color: '#ff9900', text: `Warning (${diffDays} days)`, weight: 2 };
    return { color: '#008000', text: 'Compliant', weight: 1 };
  };

  const getInitialStatus = () => {
    if (!savedData.initialDueDate) return { color: '#ccc', text: 'Awaiting Consent Date', weight: 0 };
    const diffDays = getDaysDifference(savedData.initialDueDate);
    if (diffDays === null) return { color: '#ccc', text: 'No Date Set', weight: 0 };
    if (diffDays < 0) return { color: '#8b0000', text: `Past Due (${Math.abs(diffDays)} days)`, weight: 4 };
    if (diffDays <= 14) return { color: '#ba0c2f', text: `Critical (${diffDays} days)`, weight: 3 };
    if (diffDays <= 30) return { color: '#ff9900', text: `Warning (${diffDays} days)`, weight: 2 };
    return { color: '#003366', text: 'Compliant', weight: 1 };
  };

  const annualStatus = getAnnualStatus();
  const reevalStatus = getReevalStatus();
  const initialStatus = getInitialStatus();

  // Override card color to signify safe Testing Phase
  const getDominantColor = () => {
    if (savedData.initialTracking) return initialStatus.color;
    if (savedData.consentObtainedDate) return '#0055a4'; 
    if (isConsentWarning) return '#ff9900'; 
    if (isConsentOverdue) return '#8b0000';
    return annualStatus.weight >= reevalStatus.weight ? annualStatus.color : reevalStatus.color;
  };

  // --- Quick Action Handlers for Consent ---
  const handleQuickLogConsent = async (dateVal) => {
    if (!dateVal) return;
    try {
      const studentRef = doc(db, 'districts', tenantId, 'students', student.id);
      await updateDoc(studentRef, { consentObtainedDate: dateVal });
      
      setSavedData(prev => ({ ...prev, consentObtainedDate: dateVal }));
      setConsentObtainedDate(dateVal);
      setShowConsentPicker(false);
      setTempConsentDate('');
    } catch (err) {
      console.error("Error logging consent:", err);
      alert("Failed to log consent.");
    }
  };

  const handleClearConsent = async () => {
    try {
      const studentRef = doc(db, 'districts', tenantId, 'students', student.id);
      await updateDoc(studentRef, { consentObtainedDate: '' });
      
      setSavedData(prev => ({ ...prev, consentObtainedDate: '' }));
      setConsentObtainedDate('');
    } catch (err) {
      console.error("Error clearing consent:", err);
    }
  };

  // --- Standard Form Save ---
  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');
    try {
      const studentRef = doc(db, 'districts', tenantId, 'students', student.id);
      await updateDoc(studentRef, {
        annualIepDate: annualDate,
        reevalDate: reevalDate,
        consentDate: consentDate, 
        consentObtainedDate: consentObtainedDate,
        initialDueDate: calculatedInitialDue,
        initialTracking: initialTracking
      });
      
      setSavedData({
        annualDate: annualDate,
        reevalDate: reevalDate,
        consentDate: consentDate,
        consentObtainedDate: consentObtainedDate,
        initialDueDate: calculatedInitialDue,
        initialTracking: initialTracking
      });

      setSaveMessage('Saved!');
      setTimeout(() => {
        setSaveMessage('');
        setIsEditing(false); 
      }, 1000);
    } catch (error) {
      console.error("Error saving dates:", error);
      setSaveMessage('Error saving.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setAnnualDate(savedData.annualDate);
    setReevalDate(savedData.reevalDate);
    setConsentDate(savedData.consentDate);
    setConsentObtainedDate(savedData.consentObtainedDate);
    setCalculatedInitialDue(savedData.initialDueDate);
    setInitialTracking(savedData.initialTracking);
    setIsEditing(false);
  };

  const isAdmin = currentUserRole === 'SuperAdmin' || currentUserRole === 'District Admin';

  return (
    <div style={{ 
      backgroundColor: '#fff', 
      padding: '20px', 
      borderRadius: '8px', 
      marginBottom: '15px', 
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      borderLeft: `8px solid ${getDominantColor()}`, 
      display: 'flex',
      flexDirection: 'column',
      gap: '15px'
    }}>
      
      {/* HEADER SECTION */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
        <div>
          <h3 style={{ margin: '0 0 5px 0', color: '#333' }}>{student.name || student.studentName || student.fullName || 'Student'}</h3>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', backgroundColor: '#f4f4f4', padding: '3px 8px', borderRadius: '12px', color: '#666' }}>
              Building: {student.schoolId || 'Unassigned'}
            </span>
            <span style={{ fontSize: '13px', backgroundColor: '#e6f0fa', padding: '3px 8px', borderRadius: '12px', color: '#003366', fontWeight: 'bold' }}>
              Case Manager: {caseManagerName}
            </span>
            <span style={{ fontSize: '13px', backgroundColor: '#fff3e6', padding: '3px 8px', borderRadius: '12px', color: '#cc7a00', fontWeight: 'bold' }}>
              Auditor: {eceRecorderName || 'Unassigned'}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          {!isEditing && currentUserRole !== 'ECE Recorder' && (
            <button 
              onClick={() => setIsEditing(true)}
              style={{ padding: '6px 12px', backgroundColor: '#f0f5fa', color: '#003366', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
            >
              ✎ Edit Dates
            </button>
          )}

          {isAdmin && (
            <>
              <button 
                onClick={() => setIsTransferring(!isTransferring)}
                style={{ padding: '6px 12px', backgroundColor: '#fff', color: '#003366', border: '1px solid #003366', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
              >
                {isTransferring ? 'Cancel Reassign' : 'Reassign'}
              </button>
              <button 
                onClick={() => onDelete(student.id, student.name || student.studentName || 'Student')}
                style={{ padding: '6px 12px', backgroundColor: '#fff', color: '#ba0c2f', border: '1px solid #ba0c2f', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* ADMIN TRANSFER UI */}
      {isTransferring && isAdmin && (
        <div style={{ backgroundColor: '#f4f7f6', padding: '15px', borderRadius: '6px', border: '1px solid #ccc', display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 150px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>New Building</label>
            <select value={transferSchoolId} onChange={(e) => setTransferSchoolId(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
              <option value="">-- Select School --</option>
              {schools.map((school, index) => <option key={index} value={school}>{school}</option>)}
            </select>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>New Case Manager</label>
            <select value={transferManagerId} onChange={(e) => setTransferManagerId(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
              <option value="">-- Select Case Manager --</option>
              {staffList.map(staff => <option key={staff.id} value={staff.id}>{staff.name}</option>)}
            </select>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>New Auditor (ECE)</label>
            <select value={transferEceRecorderId} onChange={(e) => setTransferEceRecorderId(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
              <option value="">-- Select ECE Recorder --</option>
              {eceRecordersList.map(staff => <option key={staff.id} value={staff.id}>{staff.name}</option>)}
            </select>
          </div>
          <button 
            onClick={() => { onTransfer(student.id, transferSchoolId, transferManagerId, transferEceRecorderId); setIsTransferring(false); }}
            style={{ padding: '9px 15px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', height: 'fit-content' }}
          >
            Confirm Transfer
          </button>
        </div>
      )}

      {/* CONDITIONAL RENDER: STATIC VAULT VIEW OR EDIT FORM */}
      {!isEditing ? (
        // --- SAVED VAULT VIEW (READS FROM savedData) ---
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
          {!savedData.initialTracking ? (
            <>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>Annual IEP Date</label>
                <div style={{ fontWeight: 'bold', color: savedData.annualDate ? '#333' : '#999', fontSize: '16px' }}>
                  {formatDateString(savedData.annualDate)} <span style={{ fontSize: '12px', color: annualStatus.color, marginLeft: '8px' }}>{annualStatus.text}</span>
                </div>
              </div>

              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>Re-Evaluation Date</label>
                <div style={{ fontWeight: 'bold', color: savedData.reevalDate ? '#333' : '#999', fontSize: '16px' }}>
                  {formatDateString(savedData.reevalDate)} <span style={{ fontSize: '12px', color: reevalStatus.color, marginLeft: '8px' }}>{reevalStatus.text}</span>
                </div>
              </div>

              {savedData.reevalDate && (
                <div style={{ flex: '1 1 100%', display: 'flex', gap: '20px', marginTop: '5px', padding: '12px', backgroundColor: savedData.consentObtainedDate ? '#e6f2ff' : (isConsentWarning ? '#fff3e6' : '#fcfcfc'), border: savedData.consentObtainedDate ? '1px solid #0055a4' : (isConsentWarning ? '1px solid #ff9900' : '1px solid #e0e0e0'), borderRadius: '6px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px', fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>
                      {savedData.consentObtainedDate ? 'Re-Evaluation Phase' : `Consent Needed By (80 Days) ${isConsentWarning ? '⚠️ 14-Day Warning' : ''}`}
                    </label>
                    
                    {/* Dynamic Consent UI */}
                    {savedData.consentObtainedDate ? (
                      <div style={{ fontWeight: 'bold', color: '#0055a4', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        ✓ Consent Logged: {formatDateString(savedData.consentObtainedDate)}
                        <button onClick={handleClearConsent} style={{ background: 'none', border: 'none', color: '#ba0c2f', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline', padding: 0 }}>Undo</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ fontWeight: 'bold', color: isConsentWarning ? '#ff9900' : (isConsentOverdue ? '#ba0c2f' : '#333'), fontSize: '15px' }}>
                          {computedConsentDue ? formatDateString(computedConsentDue) : 'Awaiting Calendar Sync'}
                        </div>
                        
                        {!showConsentPicker ? (
                           <button 
                             onClick={() => {
                               setTempConsentDate(''); // Clear any old data
                               setShowConsentPicker(true);
                             }}
                             style={{ padding: '4px 10px', backgroundColor: '#003366', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                           >
                             Log Consent
                           </button>
                        ) : (
                           <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                             <input 
                               type="date" 
                               value={tempConsentDate}
                               onChange={(e) => setTempConsentDate(e.target.value)} 
                               style={{ padding: '2px 4px', fontSize: '11px', border: '1px solid #ccc', borderRadius: '3px' }}
                             />
                             <button 
                               onClick={() => handleQuickLogConsent(tempConsentDate)} 
                               style={{ padding: '3px 8px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}
                             >
                               Save
                             </button>
                             <button 
                               onClick={() => { setShowConsentPicker(false); setTempConsentDate(''); }} 
                               style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '11px', padding: 0 }}
                             >
                               Cancel
                             </button>
                           </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px', fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>Schedule Meeting By (4 Weeks)</label>
                    <div style={{ fontWeight: 'bold', color: '#003366', fontSize: '15px' }}>
                      {formatDateString(computedMeetingDate)}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>Consent Signed Date</label>
                <div style={{ fontWeight: 'bold', color: savedData.consentDate ? '#333' : '#ba0c2f', fontSize: '16px' }}>
                  {formatDateString(savedData.consentDate)}
                </div>
              </div>

              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>Legal 60-Day Deadline</label>
                <div style={{ fontWeight: 'bold', color: savedData.initialDueDate ? '#003366' : '#999', fontSize: '16px' }}>
                  {formatDateString(savedData.initialDueDate)} <span style={{ fontSize: '12px', color: initialStatus.color, marginLeft: '8px' }}>{initialStatus.text}</span>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        // --- EDIT MODE (INTERACTIVE FORM: READS FROM DRAFTS) ---
        <div style={{ backgroundColor: '#fcfcfc', padding: '15px', border: '1px solid #ccc', borderRadius: '6px' }}>
          
          <div style={{ paddingBottom: '15px', marginBottom: '15px', borderBottom: '1px dashed #ccc' }}>
             <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold', color: '#ba0c2f' }}>
              <input 
                type="checkbox" 
                checked={initialTracking} 
                onChange={(e) => setInitialTracking(e.target.checked)} 
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              Student is currently in Initial Evaluation phase
            </label>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
            {!initialTracking ? (
              <>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>Annual IEP</label>
                  <input type="date" value={annualDate} onChange={(e) => setAnnualDate(e.target.value)} style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }} />
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>Re-Evaluation</label>
                  <input type="date" value={reevalDate} onChange={(e) => setReevalDate(e.target.value)} style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }} />
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>Consent Obtained (Testing Phase)</label>
                  <input type="date" value={consentObtainedDate} onChange={(e) => setConsentObtainedDate(e.target.value)} style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }} />
                </div>
              </>
            ) : (
              <>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>Date of Consent Signed</label>
                  <input type="date" value={consentDate} onChange={(e) => setConsentDate(e.target.value)} style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: `2px solid #ba0c2f`, borderRadius: '4px' }} />
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px', color: '#666' }}>Engine Calculation Preview</label>
                  <div style={{ width: '100%', padding: '8px', boxSizing: 'border-box', backgroundColor: '#eee', border: `1px solid #ccc`, borderRadius: '4px', fontWeight: 'bold', color: '#666' }}>
                    {calculatedInitialDue ? formatDateString(calculatedInitialDue) : 'Awaiting Date...'}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* EDIT CONTROLS */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button 
                onClick={handleCancelEdit}
                style={{ padding: '8px 16px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Cancel
              </button>
              <button 
                onClick={handleSave} 
                disabled={isSaving}
                style={{ padding: '8px 16px', backgroundColor: '#003366', color: 'white', border: 'none', borderRadius: '4px', cursor: isSaving ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}
              >
                {isSaving ? 'Saving...' : 'Save to Vault'}
              </button>
            </div>
          </div>
          
        </div>
      )}
      
    </div>
  );
};

export default StudentCard;